import { useEffect, useMemo, useState } from "react";
import { Calculator } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { money } from "@/lib/format";

const PRESETS = [20, 30, 40, 50];
const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const r2 = (v) => Math.round(n(v) * 100) / 100;

/** Sell price from a cost: markup = cost x (1+p), margin = cost / (1-p). */
export const sellFromCost = (cost, mode, pct) => {
  const p = n(pct) / 100;
  if (mode === "margin") return p >= 1 ? n(cost) : r2(n(cost) / (1 - p));
  return r2(n(cost) * (1 + p));
};

const Row = ({ label, value, strong }) => (
  <div className="flex items-center justify-between py-1 text-sm">
    <span className="text-zinc-500">{label}</span>
    <span className={`font-mono-data ${strong ? "font-bold text-zinc-100" : "text-zinc-300"}`}>
      {money(value)}
    </span>
  </div>
);

const Toggle = ({ value, onChange, options, testId }) => (
  <div className="flex overflow-hidden rounded-md border border-zinc-800" data-testid={testId}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        data-testid={`${testId}-${o.value}`}
        onClick={() => onChange(o.value)}
        className={`h-9 px-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
          value === o.value ? "bg-indigo-500 text-white" : "bg-zinc-950 text-zinc-400 hover:text-zinc-200"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export default function CostMarginDialog({ line, open, onOpenChange, onApply }) {
  const [src, setSrc] = useState(null);
  const [cost, setCost] = useState("");
  const [supplier, setSupplier] = useState("");
  const [basis, setBasis] = useState("used");
  const [mode, setMode] = useState("markup");
  const [pct, setPct] = useState(30);
  const [taxPct, setTaxPct] = useState(0);
  const [other, setOther] = useState(0);
  const [w, setW] = useState("");
  const [h, setH] = useState("");

  useEffect(() => {
    if (!open || !line) return;
    const params = { item: line.item || "", material: line.item || "", kind: line.kind || "",
                     group: line.stone_group || "" };
    api
      .get("/quotes/cost-source", { params })
      .then(({ data }) => {
        setSrc(data);
        setCost(String(line.unit_cost ?? data.unit_cost ?? 0));
        setSupplier(line.cost_supplier || data.supplier || "");
        setW(String(line.cost_width_in || data.width_in || ""));
        setH(String(line.cost_height_in || data.height_in || ""));
        setTaxPct(n(line.cost_tax_pct) || 0);
        setOther(n(line.cost_other) || 0);
        setMode(line.cost_mode || data.default_mode || "markup");
        setPct(line.cost_pct != null ? n(line.cost_pct) : n(data.default_pct) || 30);
        if (line.cost_basis) setBasis(line.cost_basis);
      })
      .catch(() => setSrc({ suppliers: [] }));
  }, [open, line]);

  const qty = n(line?.qty);
  const fullSqft = useMemo(() => r2((n(w) * n(h)) / 144), [w, h]);
  const unitsQty = basis === "full" && fullSqft > 0 ? fullSqft : qty;
  const materialCost = r2(unitsQty * n(cost));
  const taxed = r2(materialCost * (1 + n(taxPct) / 100));
  const totalCost = r2(taxed + n(other));
  const costPerUnit = qty > 0 ? r2(totalCost / qty) : totalCost;
  const unitPrice = sellFromCost(costPerUnit, mode, pct);
  const lineTotal = r2(unitPrice * qty);
  const profit = r2(lineTotal - totalCost);
  const marginPct = lineTotal > 0 ? Math.round((profit / lineTotal) * 1000) / 10 : 0;

  const last = (supplier && src?.supplier_last?.[supplier]) || null;
  const rise = last && n(cost) > n(last.unit_cost) + 0.005;
  const lastRef = last && last.source === "po" && last.ref ? ` · ${last.ref}` : "";

  const apply = () => {
    if (supplier && line?.item && n(cost) > 0) {
      api
        .post("/quotes/supplier-cost", {
          supplier,
          item: line.item,
          unit_cost: r2(cost),
        })
        .catch(() => {});
    }
    onApply({
      unit_price: unitPrice,
      unit_cost: r2(cost),
      cost_total: totalCost,
      cost_basis: basis,
      cost_mode: mode,
      cost_pct: n(pct),
      cost_supplier: supplier,
      cost_tax_pct: n(taxPct),
      cost_other: n(other),
      cost_width_in: n(w),
      cost_height_in: n(h),
      cost_full_sqft: fullSqft,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[92vh] max-w-2xl overflow-y-auto border-zinc-800 bg-zinc-950"
        data-testid="cost-margin-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Calculator size={18} weight="bold" className="text-indigo-300" />
            Cost &amp; margin · {line?.item || "Line"}
          </DialogTitle>
          <DialogDescription>
            Check the cost, add markup or margin and apply the unit price back to the line.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Supplier
            </div>
            <Select value={supplier || "none"} onValueChange={(v) => setSupplier(v === "none" ? "" : v)}>
              <SelectTrigger className="mt-1 h-10 border-zinc-800 bg-zinc-950 text-sm"
                data-testid="cost-supplier">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {(src?.suppliers || []).map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Last landed cost
            </div>
            <div className="mt-1 flex h-10 items-center justify-between rounded-md border border-zinc-800 bg-zinc-900/60 px-3">
              <span className="text-xs text-zinc-500">
                From inventory{src?.source ? ` · ${src.source}` : ""}
              </span>
              <span className="font-mono-data text-sm text-zinc-200" data-testid="cost-landed">
                {money(src?.unit_cost || 0)}
              </span>
            </div>
          </div>
        </div>

        {rise && (
          <div
            className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
            data-testid="cost-rise-warning"
          >
            <span className="text-base leading-none">▲</span>
            <span>
              Price rise — {supplier} last charged{" "}
              <span className="font-mono-data font-bold">{money(last.unit_cost)}</span>
              {last.at ? ` on ${new Date(last.at).toLocaleDateString()}` : ""}{lastRef}. You entered{" "}
              <span className="font-mono-data font-bold">{money(n(cost))}</span>.
            </span>
          </div>
        )}
        {!rise && last && (
          <div
            className="px-1 text-[11px] text-zinc-500"
            data-testid="cost-last-note"
          >
            {supplier} last charged{" "}
            <span className="font-mono-data text-zinc-400">{money(last.unit_cost)}</span>
            {last.at ? ` on ${new Date(last.at).toLocaleDateString()}` : ""}{lastRef}.
          </div>
        )}

        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Price by
            </span>
            <Toggle
              testId="cost-basis"
              value={basis}
              onChange={setBasis}
              options={[
                { value: "used", label: "Used area" },
                { value: "full", label: "Full unit" },
              ]}
            />
            <span className="font-mono-data text-xs text-zinc-500">
              {unitsQty} {line?.unit || "EA"} billed
            </span>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Width (in)</div>
              <Input data-testid="cost-width" type="number" value={w}
                onChange={(e) => setW(e.target.value)}
                className="mt-1 h-10 border-zinc-800 bg-zinc-950 text-right font-mono-data text-sm" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Height (in)</div>
              <Input data-testid="cost-height" type="number" value={h}
                onChange={(e) => setH(e.target.value)}
                className="mt-1 h-10 border-zinc-800 bg-zinc-950 text-right font-mono-data text-sm" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Full unit</div>
              <div className="mt-1 flex h-10 items-center justify-end rounded-md border border-zinc-800 bg-zinc-900/60 px-3 font-mono-data text-sm text-zinc-300"
                data-testid="cost-full-sqft">
                {fullSqft} SF
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Unit cost</div>
              <Input data-testid="cost-unit-cost" type="number" step="0.01" value={cost}
                onChange={(e) => setCost(e.target.value)}
                className="mt-1 h-10 border-zinc-800 bg-zinc-950 text-right font-mono-data text-sm" />
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Toggle
              testId="cost-mode"
              value={mode}
              onChange={setMode}
              options={[
                { value: "markup", label: "Markup" },
                { value: "margin", label: "Margin" },
              ]}
            />
            {PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                data-testid={`cost-preset-${p}`}
                onClick={() => setPct(p)}
                className={`h-9 rounded-md border px-3 text-xs font-bold transition-colors duration-150 ${
                  n(pct) === p
                    ? "border-indigo-400/60 bg-indigo-500/20 text-indigo-200"
                    : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-zinc-200"
                }`}
              >
                {p}%
              </button>
            ))}
            <div className="flex items-center gap-1">
              <Input data-testid="cost-pct" type="number" step="0.1" value={pct}
                onChange={(e) => setPct(e.target.value)}
                className="h-9 w-20 border-zinc-800 bg-zinc-950 text-right font-mono-data text-sm" />
              <span className="text-xs text-zinc-500">%</span>
            </div>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="flex items-center gap-2">
              <span className="w-28 text-[10px] uppercase tracking-widest text-zinc-500">Tax on cost</span>
              <Input data-testid="cost-tax" type="number" step="0.01" value={taxPct}
                onChange={(e) => setTaxPct(e.target.value)}
                className="h-9 w-24 border-zinc-800 bg-zinc-950 text-right font-mono-data text-sm" />
              <span className="text-xs text-zinc-500">%</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-28 text-[10px] uppercase tracking-widest text-zinc-500">Other charges</span>
              <Input data-testid="cost-other" type="number" step="0.01" value={other}
                onChange={(e) => setOther(e.target.value)}
                className="h-9 w-28 border-zinc-800 bg-zinc-950 text-right font-mono-data text-sm" />
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3" data-testid="cost-review">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Final price review
          </div>
          <div className="mt-2">
            <Row label={`Cost (${unitsQty} ${line?.unit || "EA"} × ${money(n(cost))})`} value={materialCost} />
            {n(taxPct) > 0 && <Row label={`Tax ${n(taxPct)}%`} value={r2(taxed - materialCost)} />}
            {n(other) > 0 && <Row label="Other charges" value={n(other)} />}
            <Row label="Total cost" value={totalCost} strong />
            <div className="my-2 border-t border-zinc-800" />
            <div className="flex items-center justify-between py-1 text-sm">
              <span className="text-zinc-500">Unit price ({mode} {n(pct)}%)</span>
              <span className="font-mono-data text-lg font-bold text-indigo-300" data-testid="cost-unit-price">
                {money(unitPrice)}
              </span>
            </div>
            <Row label={`Line total (${qty} ${line?.unit || "EA"})`} value={lineTotal} strong />
            <div className="flex items-center justify-between py-1 text-sm">
              <span className="text-zinc-500">Profit</span>
              <span className="font-mono-data text-emerald-300" data-testid="cost-profit">
                {money(profit)} · {marginPct}% margin
              </span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cost-cancel"
            className="border-zinc-700 bg-transparent">
            Cancel
          </Button>
          <Button onClick={apply} data-testid="cost-apply"
            className="bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400">
            Apply unit price
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
